import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Chip,
  Avatar,
  Grid,
  IconButton,
  Divider,
  Paper,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Event as EventIcon,
  Inventory as TripIcon,
  Close as CloseIcon,
  DateRange as DateIcon,
  Place as PlaceIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Photo as PhotoIcon,
  Edit as EditIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

// Interface for timeline item from parent component
interface TimelineItem {
  id: string;
  type: 'event' | 'trip';
  title: string;
  description?: string;
  startDate: Date;
  endDate?: Date;
  location?: string;
  destination?: string;
  coverImageUrl?: string;
  galleryImages?: string[];
  color?: string;
  createdBy: {
    id: string;
    name: string;
    photoUrl?: string;
  };
  participants?: number;
  participantsList?: {
    id: string;
    name: string;
    photoUrl?: string;
  }[];
  organizationId: string;
}

interface TimelineItemDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  item: TimelineItem | null;
  onViewGallery: (images: string[], itemId: string, itemType: 'event' | 'trip') => void;
  onEdit?: (itemId: string, itemType: 'event' | 'trip') => void;
  onShare?: (itemId: string, itemType: 'event' | 'trip') => void;
}

const TimelineItemDetailsDialog: React.FC<TimelineItemDetailsDialogProps> = ({
  open,
  onClose,
  item,
  onViewGallery,
  onEdit,
  onShare
}) => {
  if (!item) return null;

  const handleViewGallery = () => {
    if (item.galleryImages && item.galleryImages.length > 0) {
      onViewGallery(item.galleryImages, item.id, item.type);
      onClose();
    }
  };

  const formattedDateRange = () => {
    if (!item.startDate) return 'No date';
    
    const startDate = item.startDate instanceof Date 
      ? item.startDate 
      : new Date(item.startDate);
      
    if (!item.endDate) {
      return format(startDate, 'MMMM d, yyyy');
    }
    
    const endDate = item.endDate instanceof Date 
      ? item.endDate 
      : new Date(item.endDate);
      
    if (format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')) {
      return format(startDate, 'MMMM d, yyyy');
    }
    
    // If same month and year
    if (format(startDate, 'yyyy-MM') === format(endDate, 'yyyy-MM')) {
      return `${format(startDate, 'MMMM d')} - ${format(endDate, 'd, yyyy')}`;
    }
    
    // If same year
    if (format(startDate, 'yyyy') === format(endDate, 'yyyy')) {
      return `${format(startDate, 'MMMM d')} - ${format(endDate, 'MMMM d, yyyy')}`;
    }
    
    // Different years
    return `${format(startDate, 'MMMM d, yyyy')} - ${format(endDate, 'MMMM d, yyyy')}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {/* Cover Image */}
        {item.coverImageUrl && (
          <Box
            sx={{
              height: 240,
              width: '100%',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Box
              component="img"
              src={item.coverImageUrl}
              alt={item.title}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.6) 100%)'
              }}
            />
          </Box>
        )}

        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: item.coverImageUrl ? 'white' : 'action.active',
            bgcolor: item.coverImageUrl ? 'rgba(0,0,0,0.4)' : 'transparent',
            '&:hover': {
              bgcolor: item.coverImageUrl ? 'rgba(0,0,0,0.6)' : 'action.hover'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Content */}
        <DialogContent sx={{ p: 3, pt: item.coverImageUrl ? 3 : 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip
                  icon={item.type === 'event' ? <EventIcon /> : <TripIcon />}
                  label={item.type === 'event' ? 'Event' : 'Trip'}
                  color={item.type === 'event' ? 'primary' : 'secondary'}
                  sx={{ mr: 1 }}
                />
                {item.type === 'event' && item.participants && item.participants > 0 && (
                  <Chip
                    icon={<PeopleIcon fontSize="small" />}
                    label={`${item.participants} ${item.participants === 1 ? 'Attendee' : 'Attendees'}`}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
              <Typography variant="h4" gutterBottom component="h2" sx={{ fontWeight: 'bold' }}>
                {item.title}
              </Typography>
            </Box>
            
            <Box>
              {item.galleryImages && item.galleryImages.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<PhotoIcon />}
                  onClick={handleViewGallery}
                  sx={{ mr: 1 }}
                >
                  View Photos ({item.galleryImages.length})
                </Button>
              )}
              {onEdit && (
                <IconButton onClick={() => onEdit(item.id, item.type)}>
                  <EditIcon />
                </IconButton>
              )}
              {onShare && (
                <IconButton onClick={() => onShare(item.id, item.type)}>
                  <ShareIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {/* Description */}
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, mb: 3 }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {item.description || 'No description provided.'}
                </Typography>
              </Paper>

              {/* Created by */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mr: 1 }}>Created by:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    src={item.createdBy.photoUrl} 
                    sx={{ width: 24, height: 24, mr: 1 }}
                  >
                    {item.createdBy.name.charAt(0)}
                  </Avatar>
                  <Typography variant="body2">{item.createdBy.name}</Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                {/* Date */}
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DateIcon color="action" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2">Date</Typography>
                  </Box>
                  <Typography variant="body2">
                    {formattedDateRange()}
                  </Typography>
                </Paper>

                {/* Location */}
                {(item.location || item.destination) && (
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PlaceIcon color="action" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">
                        {item.type === 'trip' ? 'Destination' : 'Location'}
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      {item.location || item.destination}
                    </Typography>
                  </Paper>
                )}

                {/* Participants */}
                {item.participantsList && item.participantsList.length > 0 && (
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PeopleIcon color="action" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">
                        {item.type === 'trip' ? 'Travelers' : 'Attendees'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {item.participantsList.slice(0, 10).map((participant) => (
                        <Tooltip key={participant.id} title={participant.name}>
                          <Avatar 
                            src={participant.photoUrl} 
                            sx={{ width: 32, height: 32 }}
                          >
                            {participant.name.charAt(0)}
                          </Avatar>
                        </Tooltip>
                      ))}
                      {item.participantsList.length > 10 && (
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'action.disabled' }}>
                          +{item.participantsList.length - 10}
                        </Avatar>
                      )}
                    </Box>
                  </Paper>
                )}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose}>Close</Button>
          <Button 
            variant="contained" 
            onClick={() => window.open(`/organizations/${item.organizationId}/${item.type}s/${item.id}`, '_blank')}
          >
            Open Full {item.type === 'event' ? 'Event' : 'Trip'} Page
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default TimelineItemDetailsDialog;
